import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import { getAdminPublicConfig } from "../config/public-config";

type CachedAdminSupabaseClient = Readonly<{
  url: string;
  publishableKey: string;
  client: SupabaseClient;
}>;

let cachedClient: CachedAdminSupabaseClient | null = null;

export function createAdminSupabaseClient(): SupabaseClient | null {
  const config = getAdminPublicConfig();
  if (!config.supabaseUrl || !config.supabasePublishableKey) return null;

  const url = config.supabaseUrl.toString();
  const publishableKey = config.supabasePublishableKey;
  if (cachedClient?.url === url && cachedClient.publishableKey === publishableKey) {
    return cachedClient.client;
  }

  const client = createClient(url, publishableKey, {
    auth: {
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: "pkce",
      persistSession: true,
    },
  });
  cachedClient = { url, publishableKey, client };
  return client;
}

/**
 * A client with no storage, no refresh timer and no URL detection, for one server check that must not touch the session of this browser
 * (the one-time `token_hash` of a recovery link). `null` when Supabase is not configured. Used once and dropped.
 */
export function createEphemeralAdminSupabaseClient(): SupabaseClient | null {
  const config = getAdminPublicConfig();
  if (!config.supabaseUrl || !config.supabasePublishableKey) return null;
  return createClient(config.supabaseUrl.toString(), config.supabasePublishableKey, {
    auth: {
      autoRefreshToken: false,
      detectSessionInUrl: false,
      flowType: "pkce",
      persistSession: false,
    },
  });
}
